import {
  useReducer,
  useContext,
  createContext,
  useEffect,
  useCallback,
} from "react";
import { useAuth } from "./AuthContext";

const baseUrl = process.env.REACT_APP_BASE_URL;

const RoomsContext = createContext();

const initialState = {
  rooms: [],
  roomsIsLoading: false,
  currentRoom: null,
  members: [],
  messages: [],
  timeline: [],
  mapLocations: [],
  events: [],
  error: null,
};

// ...existing code...
// ...existing code...

function reducer(state, action) {
  switch (action.type) {
    case "loading":
      return { ...state, roomsIsLoading: true, error: null };
    case "rooms/loaded":
      return { ...state, roomsIsLoading: false, rooms: action.payload || [] };
    case "room/loaded":
      return { ...state, roomsIsLoading: false, currentRoom: action.payload };
    case "members/loaded":
      return { ...state, roomsIsLoading: false, members: action.payload || [] };
    case "messages/loaded":
      return {
        ...state,
        roomsIsLoading: false,
        messages: action.payload || [],
      };
    case "timeline/loaded":
      return {
        ...state,
        roomsIsLoading: false,
        timeline: action.payload || [],
      };
    case "map/loaded":
      return {
        ...state,
        roomsIsLoading: false,
        mapLocations: action.payload || [],
      };
    case "events/loaded": {
      const newEvents = action.payload || [];
      // Always update events state to ensure UI reflects latest changes
      return {
        ...state,
        roomsIsLoading: false,
        events: newEvents,
      };
    }
    case "rejected":
      return { ...state, roomsIsLoading: false, error: action.payload };
    default:
      throw new Error("Unknown action type");
  }
}

function RoomsProvider({ children }) {
  const { user, isLoading, fetchWithToken } = useAuth();
  const [state, dispatch] = useReducer(reducer, initialState);

  // MEMORY ROOM LOCATIONS API
  // Memoize fetchRoomLocations so its reference is stable
  const fetchRoomLocations = useCallback(
    async (roomId) => {
      if (!roomId) return;
      dispatch({ type: "loading" });
      try {
        const res = await fetchWithToken(
          `${baseUrl}/rooms/room/${roomId}/locations`,
        );
        const data = await res.json();
        if (!res.ok)
          throw new Error(data?.error || data?.message || res.statusText);
        dispatch({ type: "map/loaded", payload: data.data || [] });
        return data.data;
      } catch (err) {
        dispatch({ type: "rejected", payload: err.message || err });
        throw err;
      }
    },
    [fetchWithToken, baseUrl],
  );

  const createRoomLocation = useCallback(
    async (roomId, location) => {
      dispatch({ type: "loading" });
      try {
        const res = await fetchWithToken(
          `${baseUrl}/rooms/room/${roomId}/locations`,
          {
            method: "POST",
            body: JSON.stringify(location),
            headers: { "Content-Type": "application/json" },
          },
        );
        const data = await res.json();
        if (!res.ok)
          throw new Error(data?.error || data?.message || res.statusText);
        // After creating, fetch the updated locations list
        const locations = await fetchRoomLocations(roomId);
        return data.location;
      } catch (err) {
        dispatch({ type: "rejected", payload: err.message || err });
        throw err;
      }
    },
    [fetchWithToken, fetchRoomLocations],
  );

  const updateRoomLocation = useCallback(
    async (roomId, locId, location) => {
      dispatch({ type: "loading" });
      try {
        const res = await fetchWithToken(
          `${baseUrl}/rooms/room/${roomId}/locations/${locId}`,
          {
            method: "PUT",
            body: JSON.stringify(location),
            headers: { "Content-Type": "application/json" },
          },
        );
        const data = await res.json();
        if (!res.ok)
          throw new Error(data?.error || data?.message || res.statusText);
        await fetchRoomLocations(roomId);
        return data.location;
      } catch (err) {
        dispatch({ type: "rejected", payload: err.message || err });
        throw err;
      }
    },
    [fetchWithToken, fetchRoomLocations],
  );

  const deleteRoomLocation = useCallback(
    async (roomId, locId) => {
      dispatch({ type: "loading" });
      try {
        const res = await fetchWithToken(
          `${baseUrl}/rooms/room/${roomId}/locations/${locId}`,
          {
            method: "DELETE",
          },
        );
        const data = await res.json();
        if (!res.ok)
          throw new Error(data?.error || data?.message || res.statusText);
        dispatch({ type: "map/loaded", payload: data.locations || [] });
        return true;
      } catch (err) {
        dispatch({ type: "rejected", payload: err.message || err });
        throw err;
      }
    },
    [fetchWithToken],
  );

  // EVENTS API
  const fetchEvents = useCallback(
    async (roomid) => {
      if (!roomid) return;
      try {
        const res = await fetchWithToken(`${baseUrl}/events/room/${roomid}`);
        const text = await res.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch (jsonErr) {
          throw new Error(`Invalid JSON from server: ${text}`);
        }
        if (!res.ok) {
          const msg = data?.error || data?.message || text || res.statusText;
          throw new Error(`API error: ${msg}`);
        }
        dispatch({ type: "events/loaded", payload: data.data || [] });
        return data.data;
      } catch (err) {
        console.error("fetchEvents error:", err);
        dispatch({ type: "rejected", payload: err.message || err });
        throw err;
      }
    },
    [fetchWithToken],
  );

  const createEvent = useCallback(
    async (event) => {
      try {
        const res = await fetchWithToken(`${baseUrl}/events/`, {
          method: "POST",
          body: JSON.stringify(event),
          headers: { "Content-Type": "application/json" },
        });
        const text = await res.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch (jsonErr) {
          throw new Error(`Invalid JSON from server: ${text}`);
        }
        if (!res.ok) {
          const msg = data?.error || data?.message || text || res.statusText;
          throw new Error(`API error: ${msg}`);
        }
        await fetchEvents(event.RoomID);
        return data;
      } catch (err) {
        console.error("createEvent error:", err);
        dispatch({ type: "rejected", payload: err.message || err });
        throw err;
      }
    },
    [fetchWithToken, fetchEvents],
  );

  const updateEvent = useCallback(
    async (eventid, patch) => {
      try {
        console.log(
          "updateEvent called with eventid:",
          eventid,
          "patch:",
          patch,
        );
        let options = { method: "PUT" };
        let isFormData = patch instanceof FormData;
        if (isFormData) {
          options.body = patch;
          // Do NOT set Content-Type header for FormData; browser will handle it
        } else {
          options.body = JSON.stringify(patch);
          options.headers = { "Content-Type": "application/json" };
        }
        const res = await fetchWithToken(
          `${baseUrl}/events/${eventid}`,
          options,
        );
        const text = await res.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch (jsonErr) {
          throw new Error(`Invalid JSON from server: ${text}`);
        }
        if (!res.ok) {
          const msg = data?.error || data?.message || text || res.statusText;
          throw new Error(`API error: ${msg}`);
        }
        // If RoomID is present, refresh events for that room
        if (
          (isFormData && patch.get && patch.get("RoomID")) ||
          (!isFormData && patch.RoomID)
        ) {
          const roomId = isFormData ? patch.get("RoomID") : patch.RoomID;
          if (roomId) await fetchEvents(roomId);
        }
        dispatch({ type: "events/loaded", payload: data.data || [] });
        return data;
      } catch (err) {
        console.error("updateEvent error:", err);
        dispatch({ type: "rejected", payload: err.message || err });
        throw err;
      }
    },
    [fetchWithToken, fetchEvents],
  );

  const deleteEvent = useCallback(
    async (eventid, roomid) => {
      try {
        const res = await fetchWithToken(`${baseUrl}/events/${eventid}`, {
          method: "DELETE",
        });
        const text = await res.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch (jsonErr) {
          // DELETE may return empty, so only warn if not empty
          if (text && text.trim())
            throw new Error(`Invalid JSON from server: ${text}`);
        }
        if (!res.ok) {
          const msg = data?.error || data?.message || text || res.statusText;
          throw new Error(`API error: ${msg}`);
        }
        await fetchEvents(roomid);
        return data;
      } catch (err) {
        console.error("deleteEvent error:", err);
        dispatch({ type: "rejected", payload: err.message || err });
        throw err;
      }
    },
    [fetchWithToken, fetchEvents],
  );

  const fetchRooms = useCallback(async () => {
    if (!user?.CustUUID) return;
    dispatch({ type: "loading" });
    try {
      const res = await fetchWithToken(`${baseUrl}/rooms/${user.CustUUID}`);
      const data = await res.json();
      dispatch({ type: "rooms/loaded", payload: data.data || [] });
    } catch (err) {
      dispatch({ type: "rejected", payload: err.message || err });
    }
  }, [user, fetchWithToken]);

  const searchRooms = useCallback(
    async (titleQuery) => {
      const query = (titleQuery || "").trim();
      if (!query || !user?.CustUUID) return [];

      const res = await fetchWithToken(
        `${baseUrl}/rooms/discover?title=${encodeURIComponent(query)}`,
      );
      const data = await res.json();
      return data?.data || data?.rooms || [];
    },
    [fetchWithToken, user],
  );

  const joinRoom = useCallback(
    async (roomid) => {
      if (!roomid || !user?.CustUUID) {
        throw new Error("roomid and user are required to join a room.");
      }

      const res = await fetchWithToken(`${baseUrl}/rooms/room/${roomid}/join`, {
        method: "POST",
      });
      const result = await res.json();

      await fetchRooms();
      return result;
    },
    [fetchRooms, fetchWithToken, user],
  );

  const getRoom = useCallback(
    async (roomid) => {
      if (!roomid) return;
      dispatch({ type: "loading" });
      try {
        const res = await fetchWithToken(`${baseUrl}/rooms/room/${roomid}`);
        const data = await res.json();
        dispatch({ type: "room/loaded", payload: data.data || null });
        return data.data;
      } catch (err) {
        dispatch({ type: "rejected", payload: err.message || err });
        throw err;
      }
    },
    [fetchWithToken],
  );

  const createRoom = useCallback(
    async (room) => {
      dispatch({ type: "loading" });
      try {
        const res = await fetchWithToken(`${baseUrl}/rooms/`, {
          method: "POST",
          body: JSON.stringify(room),
          headers: { "Content-Type": "application/json" },
        });
        const data = await res.json();
        await fetchRooms();
        return data;
      } catch (err) {
        dispatch({ type: "rejected", payload: err.message || err });
        throw err;
      }
    },
    [fetchWithToken, fetchRooms],
  );

  const updateRoom = useCallback(
    async (roomid, patch) => {
      dispatch({ type: "loading" });
      try {
        const res = await fetchWithToken(`${baseUrl}/rooms/room/${roomid}`, {
          method: "POST",
          body: JSON.stringify(patch),
          headers: { "Content-Type": "application/json" },
        });
        const data = await res.json();
        await getRoom(roomid);
        return data;
      } catch (err) {
        dispatch({ type: "rejected", payload: err.message || err });
        throw err;
      }
    },
    [fetchWithToken, getRoom],
  );

  const deleteRoom = useCallback(
    async (roomid) => {
      dispatch({ type: "loading" });
      try {
        const res = await fetchWithToken(`${baseUrl}/rooms/room/${roomid}`, {
          method: "DELETE",
        });
        await fetchRooms();
        return res;
      } catch (err) {
        dispatch({ type: "rejected", payload: err.message || err });
        throw err;
      }
    },
    [fetchWithToken, fetchRooms],
  );

  const listMembers = useCallback(
    async (roomid) => {
      if (!roomid) return;
      dispatch({ type: "loading" });
      try {
        const res = await fetchWithToken(
          `${baseUrl}/rooms/room/${roomid}/members`,
        );
        const data = await res.json();
        dispatch({ type: "members/loaded", payload: data.data || [] });
        return data.data;
      } catch (err) {
        dispatch({ type: "rejected", payload: err.message || err });
        throw err;
      }
    },
    [fetchWithToken],
  );

  const addMember = useCallback(
    async (roomid, member) => {
      try {
        const res = await fetchWithToken(
          `${baseUrl}/rooms/room/${roomid}/members`,
          {
            method: "POST",
            body: JSON.stringify(member),
            headers: { "Content-Type": "application/json" },
          },
        );
        const data = await res.json();
        await listMembers(roomid);
        return data;
      } catch (err) {
        dispatch({ type: "rejected", payload: err.message || err });
        throw err;
      }
    },
    [fetchWithToken, listMembers],
  );

  const removeMember = useCallback(
    async (roomid, memberid) => {
      try {
        const res = await fetchWithToken(
          `${baseUrl}/rooms/room/${roomid}/members/${memberid}`,
          {
            method: "DELETE",
          },
        );
        await listMembers(roomid);
        return res;
      } catch (err) {
        dispatch({ type: "rejected", payload: err.message || err });
        throw err;
      }
    },
    [fetchWithToken, listMembers],
  );

  const updateMemberRole = useCallback(
    async (roomid, memberid, Role) => {
      try {
        const res = await fetchWithToken(
          `${baseUrl}/rooms/room/${roomid}/members/${memberid}`,
          {
            method: "PUT",
            body: JSON.stringify({ Role }),
            headers: { "Content-Type": "application/json" },
          },
        );
        const data = await res.json();
        await listMembers(roomid);
        return data;
      } catch (err) {
        dispatch({ type: "rejected", payload: err.message || err });
        throw err;
      }
    },
    [fetchWithToken, listMembers],
  );

  const fetchMessages = useCallback(
    async (roomid, options = {}) => {
      if (!roomid) return;
      try {
        const params = new URLSearchParams();
        if (options.limit) params.set("limit", String(options.limit));
        if (options.beforeMessageId) {
          params.set("beforeMessageId", String(options.beforeMessageId));
        }

        const queryString = params.toString();
        const url = `${baseUrl}/rooms/room/${roomid}/messages${
          queryString ? `?${queryString}` : ""
        }`;

        const res = await fetchWithToken(url);
        const data = await res.json();

        if (!options.limit && !options.beforeMessageId) {
          dispatch({ type: "messages/loaded", payload: data.data || [] });
        }

        return {
          data: data.data || [],
          pagination: data.pagination || null,
        };
      } catch (err) {
        dispatch({ type: "rejected", payload: err.message || err });
        throw err;
      }
    },
    [fetchWithToken],
  );

  const toggleMessageLike = useCallback(
    async (roomid, messageid) => {
      if (!roomid || !messageid) return null;
      try {
        const res = await fetchWithToken(
          `${baseUrl}/rooms/room/${roomid}/messages/${messageid}/like`,
          { method: "POST" },
        );
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data?.error || data?.message || res.statusText);
        }
        return data;
      } catch (err) {
        dispatch({ type: "rejected", payload: err.message || err });
        throw err;
      }
    },
    [fetchWithToken],
  );

  const fetchRoomEventDetail = useCallback(
    async (roomid, eventid, options = {}) => {
      if (!roomid || !eventid) {
        throw new Error("roomid and eventid are required");
      }
      try {
        const params = new URLSearchParams();
        if (options.limit) params.set("limit", String(options.limit));
        if (options.beforeMessageId) {
          params.set("beforeMessageId", String(options.beforeMessageId));
        }
        const queryString = params.toString();

        const res = await fetchWithToken(
          `${baseUrl}/rooms/room/${roomid}/events/${eventid}/detail${
            queryString ? `?${queryString}` : ""
          }`,
        );
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data?.error || data?.message || res.statusText);
        }
        return data.data;
      } catch (err) {
        dispatch({ type: "rejected", payload: err.message || err });
        throw err;
      }
    },
    [fetchWithToken],
  );

  const fetchRoomLocationDetail = useCallback(
    async (roomid, locid, options = {}) => {
      if (!roomid || !locid) {
        throw new Error("roomid and locid are required");
      }
      try {
        const params = new URLSearchParams();
        if (options.limit) params.set("limit", String(options.limit));
        if (options.beforeMessageId) {
          params.set("beforeMessageId", String(options.beforeMessageId));
        }
        const queryString = params.toString();

        const res = await fetchWithToken(
          `${baseUrl}/rooms/room/${roomid}/locations/${locid}/detail${
            queryString ? `?${queryString}` : ""
          }`,
        );
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data?.error || data?.message || res.statusText);
        }
        return data.data;
      } catch (err) {
        dispatch({ type: "rejected", payload: err.message || err });
        throw err;
      }
    },
    [fetchWithToken],
  );

  const postMessage = useCallback(
    async (roomid, messageOrFormData) => {
      try {
        let options;
        if (messageOrFormData instanceof FormData) {
          options = {
            method: "POST",
            body: messageOrFormData,
            // No headers: browser sets multipart automatically
          };
        } else {
          options = {
            method: "POST",
            body: JSON.stringify({ Message: messageOrFormData }),
            headers: { "Content-Type": "application/json" },
          };
        }
        const res = await fetchWithToken(
          `${baseUrl}/rooms/room/${roomid}/messages`,
          options,
        );
        const data = await res.json();
        return data;
      } catch (err) {
        dispatch({ type: "rejected", payload: err.message || err });
        throw err;
      }
    },
    [fetchWithToken],
  );

  const fetchTimeline = useCallback(
    async (roomid) => {
      if (!roomid) return;
      try {
        const res = await fetchWithToken(
          `${baseUrl}/rooms/room/${roomid}/timeline`,
        );
        const data = await res.json();
        dispatch({ type: "timeline/loaded", payload: data.data || [] });
        return data.data;
      } catch (err) {
        dispatch({ type: "rejected", payload: err.message || err });
        throw err;
      }
    },
    [fetchWithToken],
  );

  const fetchMap = useCallback(
    async (roomid) => {
      if (!roomid) return;
      try {
        const res = await fetchWithToken(`${baseUrl}/rooms/room/${roomid}/map`);
        const data = await res.json();
        dispatch({ type: "map/loaded", payload: data.data || [] });
        return data.data;
      } catch (err) {
        dispatch({ type: "rejected", payload: err.message || err });
        throw err;
      }
    },
    [fetchWithToken],
  );

  const fetchRoomMedia = useCallback(
    async (roomid) => {
      if (!roomid) return [];

      const res = await fetchWithToken(`${baseUrl}/rooms/room/${roomid}/media`);
      const data = await res.json();
      return data?.data || [];
    },
    [fetchWithToken],
  );

  const uploadRoomMedia = useCallback(
    async (roomid, file) => {
      if (!roomid || !file) {
        throw new Error("roomid and file are required");
      }

      const formData = new FormData();
      formData.append("files", file);

      const res = await fetchWithToken(`${baseUrl}/rooms/room/${roomid}/media`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      return data;
    },
    [fetchWithToken],
  );

  useEffect(() => {
    if (user?.CustUUID) fetchRooms();
  }, [user, fetchRooms]);

  return (
    <RoomsContext.Provider
      value={{
        ...state,
        fetchRooms,
        searchRooms,
        joinRoom,
        getRoom,
        createRoom,
        updateRoom,
        deleteRoom,
        listMembers,
        addMember,
        removeMember,
        fetchMessages,
        toggleMessageLike,
        fetchRoomEventDetail,
        fetchRoomLocationDetail,
        postMessage,
        fetchTimeline,
        fetchMap,
        fetchRoomMedia,
        uploadRoomMedia,
        updateMemberRole,
        // Events
        fetchEvents,
        createEvent,
        updateEvent,
        deleteEvent,
        // Memory Room Locations
        fetchRoomLocations,
        createRoomLocation,
        updateRoomLocation,
        deleteRoomLocation,
      }}
    >
      {children}
    </RoomsContext.Provider>
  );
}

function useRooms() {
  const context = useContext(RoomsContext);
  if (context === undefined)
    throw new Error("RoomsContext used outside RoomsProvider");
  return context;
}

export { RoomsProvider, useRooms };
